import { LoadingSpinner } from './LoadingSpinner';

/**
 * Primary CTA classes. Rests on emerald-600 — Diffit's brand green (#00875C, see
 * tailwind.config.cjs) — and swaps to a muted grey while busy so the button reads as
 * inert rather than as a lighter shade of the active green.
 *
 * The `disabled:` modifiers only ever apply to the `book_selection` variant, which is the one
 * question type that can be disabled while NOT busy (chapters not yet picked). For the other three
 * variants `disabled` is exactly the busy condition, so they always take the grey branch instead.
 *
 * @param {boolean} busy - Whether a submission or upload is in flight.
 */
const ctaClasses = (busy) =>
  `w-full py-4 px-6 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
    busy
      ? 'bg-gray-200 text-gray-400 cursor-wait'
      : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed'
  }`;

/**
 * Secondary "skip" classes — a text link that becomes a rounded-corner button on hover, set
 * slightly lower. Padding/text size are kept in sync with the Back button in FollowUpQuestions.jsx
 * so the two secondary controls read as the same size.
 */
const skipClasses =
  'self-center w-fit mt-2 px-4 py-2 rounded-lg text-gray-500 text-sm transition-colors enabled:hover:bg-gray-100 enabled:hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Renders context-sensitive continue/skip buttons based on the current question type.
 * @param {string} questionType - The type of question ('book_selection' | 'source_content_request' | 'open_ended' | 'regular').
 * @param {boolean} isLoading - Whether a submission is in progress.
 * @param {Function} onContinue - Callback when the continue/submit button is clicked.
 * @param {Function} onSkip - Callback when the skip button is clicked.
 * @param {boolean} bookChaptersSelected - Whether both start and end chapters are selected (book_selection only).
 * @param {Function} onSkipBook - Callback to skip the book and proceed with the topic (book_selection only).
 * @param {boolean} isUploadingFile - Whether a file upload is in progress (source_content_request only).
 * @param {boolean} isUploadingImage - Whether an image upload is in progress (source_content_request only).
 * @param {boolean} hasSourceContent - Whether the user has provided any source content (source_content_request only).
 */
export const ActionButtons = ({
  questionType,
  isLoading,
  onContinue,
  onSkip,
  // Book selection
  bookChaptersSelected,
  onSkipBook,
  // Source content
  isUploadingFile,
  isUploadingImage,
  hasSourceContent,
}) => {
  if (questionType === 'book_selection') {
    return (
      <div className='flex flex-col gap-3 mb-20'>
        <button
          onClick={onContinue}
          disabled={!bookChaptersSelected || isLoading}
          className={ctaClasses(isLoading)}>
          {isLoading ? (
            <>
              <LoadingSpinner />
              <span>Loading</span>
            </>
          ) : (
            'Yes, use this book'
          )}
        </button>
        <button onClick={onSkipBook} disabled={isLoading} className={skipClasses}>
          No, proceed with my topic
        </button>
      </div>
    );
  }

  if (questionType === 'source_content_request') {
    return (
      <div className='flex flex-col gap-3 mb-20'>
        <button
          onClick={onContinue}
          disabled={isLoading || isUploadingFile || isUploadingImage}
          className={ctaClasses(isLoading || isUploadingFile || isUploadingImage)}>
          {isLoading ? (
            <>
              <LoadingSpinner />
              <span>Loading</span>
            </>
          ) : isUploadingFile || isUploadingImage ? (
            <>
              <LoadingSpinner />
              <span>Uploading</span>
            </>
          ) : !hasSourceContent ? (
            'Choose content for me'
          ) : (
            'Continue'
          )}
        </button>
        <button
          onClick={onSkip}
          disabled={isLoading || isUploadingFile || isUploadingImage}
          className={skipClasses}>
          Skip
        </button>
      </div>
    );
  }

  if (questionType === 'open_ended') {
    return (
      <div className='flex flex-col gap-3 mb-20'>
        <button onClick={onContinue} disabled={isLoading} className={ctaClasses(isLoading)}>
          {isLoading ? (
            <>
              <LoadingSpinner />
              <span>Generating</span>
            </>
          ) : (
            'Generate'
          )}
        </button>
      </div>
    );
  }

  // Regular question
  return (
    <div className='flex flex-col gap-3 mb-20'>
      <button onClick={onContinue} disabled={isLoading} className={ctaClasses(isLoading)}>
        {isLoading ? (
          <>
            <LoadingSpinner />
            <span>Loading</span>
          </>
        ) : (
          'Continue'
        )}
      </button>
      <button onClick={onSkip} disabled={isLoading} className={skipClasses}>
        Skip all
      </button>
    </div>
  );
};
